<template>
  <SlideOver
    v-model="open"
    id="client-task-slide-over"
    :title="title"
    :loading-overlay="loading || isFetching"
    :closeable="(!isDeleteModalVisible && !loading) || isFetching"
  >
    <ClientTaskSlideOverHeader
      v-model:is-done="editClientTask.done"
      v-model:assignee="editClientTask.assignee"
      :rollout-id="rolloutId"
      :is-edit-mode="isEditing"
    />
    <DividerHeader :title="$t('general.details')" />

    <ClientBox
      v-if="editClientTask.client?._id"
      :client-id="editClientTask.client?._id"
      :is-editing="isEditing"
      :show-open-client-button="showOpenClientButton"
      :task-identifier="editClientTask.taskIdentifier"
      @clear-client="clearClient"
      @client-link-opened="cancel"
    />

    <div v-if="!isEditing" v-show="!editClientTask.client">
      <Label class="!leading-0 mt-0" required>
        {{ $t('general.client') }}
      </Label>
      <Select
        v-model="editClientTask.client"
        :placeholder="$t('inventory.tasks.select-client')"
        :auto-complete-collection="DATABASE_AUTOCOMPLETE_COLLECTION.Client"
        :query-params="{ rolloutId: rolloutId }"
      />
    </div>

    <div class="grid grid-cols-5 gap-4">
      <Input
        v-model="editClientTask.name"
        id="name"
        :label="$t('general.name')"
        class="col-span-3"
        required
      />

      <SelectNative
        v-model="editClientTask.priority"
        :options="priorityOptions"
        :get-display-name="getPriorityDisplayName"
        :label="$t('general.priority')"
        class="col-span-2"
      />
    </div>

    <Textarea
      v-model="editClientTask.description"
      id="description"
      :label="$t('general.description')"
      required
    />

    <Alert
      v-if="editClientTask.comment"
      type="warning"
      :title="$t('inventory.tasks.client-user-comment')"
    >
      {{ editClientTask.comment }}
    </Alert>

    <DividerHeader :title="$t('general.schedule')" />

    <div class="grid grid-cols-5 gap-4">
      <div class="col-span-3">
        <Label>
          {{ $t('general.due-date') }}
        </Label>

        <Datepicker
          v-model="selectedDate"
          mode="date-time"
          appearance="input"
          class="inline-block"
        />
      </div>

      <Select
        v-model="selectedDuration"
        :options="filteredDurationOptions"
        id-key="key"
        :label="$t('general.duration')"
        :clearable="false"
        :disabled="isDurationDisabled"
        class="col-span-2"
      />
    </div>

    <ClientTaskSlideOverEmailSection
      v-if="selectedDate.date"
      v-model:recipients="editClientTask.recipients"
      :client="editClientTask.client"
      :email-custom-field="editClientTask.emailCustomField"
    />

    <template #footer>
      <Button
        v-if="isEditing"
        type="secondary"
        size="lg"
        icon-only
        :disabled="loading"
       
        @click="isDeleteModalVisible = true"
      >
        <TrashIcon class="size-4 text-red-500" aria-hidden="true" />
      </Button>
      <Button type="secondary" size="lg" :disabled="loading" @click="cancel">
        {{ $t('general.cancel') }}
      </Button>
      <Button
        size="lg"
        :disabled="isSaveDisabled"
        :loading="loading"
       
        @click="save"
      >
        {{ $t('general.save') }}
      </Button>
    </template>

    <ModalConfirmDelete
      v-model="isDeleteModalVisible"
      :title="$t('inventory.tasks.delete-title')"
      :text="$t('inventory.tasks.delete-text')"
      :loading="loadingDelete"
      @confirmed="deleteTask"
    />
  </SlideOver>
</template>

<script lang="ts">
export const defaultClientTask: EditClientTask | CalendarClientTask = {
  name: '',
  priority: 'medium',
  duration: 0,
  description: '',
  done: false,
  client: undefined,
  assignee: undefined,
  startTime: new Date(),
  endTime: new Date(),
  rollout: { _id: '' },
  recipients: [],
};
</script>

<script lang="ts" setup>
import { klona } from 'klona';
import { isEqual } from 'lodash';
import { TrashIcon } from '@heroicons/vue/20/solid';
import type {
  CreateClientTask,
  EditClientTask,
  CalendarClientTask,
  Client,
  DatepickerValue,
  Rollout,
  DurationOption,
  Priority,
} from '@/types';
import { DATABASE_AUTOCOMPLETE_COLLECTION } from '@/types';
import { addMinutes } from '@/util/date-utils';
import { getDurationOptions } from '@/util/client-task-utils';
import { ErrorType } from '@/types/error';
import { useApiClientTask } from '~/composables/api-services/api-client-task';

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  preselectedClient: {
    type: Object as PropType<Client>,
  },
  clientTaskId: {
    type: String,
  },
  showOpenClientButton: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'saved', 'deleted']);

const { $client } = useNuxtApp();
const i18n = useI18n();
const { selectedRollout } = useRolloutRouting();
const apiClientTask = useApiClientTask();

const isEditing = computed(() => !!editClientTask.value._id);

const editClientTask = ref<EditClientTask>(klona(defaultClientTask));
const rolloutId = computed(() =>
  typeof editClientTask.value?.client?.rollout === 'object' &&
  editClientTask.value?.client?.rollout?._id
    ? editClientTask.value?.client?.rollout?._id
    : (editClientTask.value?.client?.rollout ??
      (selectedRollout.value as Rollout)?._id)
);

const isDurationDisabled = computed(
  () =>
    typeof datepickerValue.value.date === 'undefined' ||
    typeof datepickerValue.value.time?.h === 'undefined'
);

const priorityOptionsDisplayNameMap: Record<Priority, string> = {
  high: i18n.t('inventory.tasks.priority.high'),
  medium: i18n.t('inventory.tasks.priority.medium'),
  low: i18n.t('inventory.tasks.priority.low'),
};

const priorityOptions = Object.keys(priorityOptionsDisplayNameMap);
const getPriorityDisplayName = (option: Priority) => {
  return priorityOptionsDisplayNameMap[option];
};

const durationOptions: DurationOption[] = getDurationOptions(i18n);

const filteredDurationOptions = computed<DurationOption[]>(() => {
  if (
    typeof selectedDate.value.date !== 'undefined' &&
    typeof selectedDate.value.time?.h !== 'undefined'
  ) {
    return durationOptions.filter((option) => option.key !== 0);
  }

  return durationOptions;
});

const loading = ref(false);
const loadingDelete = ref(false);
const isDeleteModalVisible = ref(false);

const initialClientTask = ref(defaultClientTask);

const hasChanged = computed(
  () => !isEqual(initialClientTask.value, editClientTask.value)
);

const selectedDuration = computed<DurationOption>({
  get: () =>
    durationOptions.find(
      (option) => option.key === editClientTask.value.duration
    ) ?? durationOptions[0],
  set: (newValue: DurationOption) =>
    (editClientTask.value.duration = newValue.key),
});

const datepickerValue = ref<DatepickerValue>({
  date: undefined,
  time: undefined,
});

const defaultDurationKey = 60;
const noDurationKey = 0;

const selectedDate = computed<DatepickerValue>({
  get: () => datepickerValue.value,
  set: (newValue) => {
    if (newValue.date) {
      const date = newValue.date as Date;
      if (typeof newValue.time?.h !== 'undefined') {
        date.setHours(newValue.time?.h, newValue.time?.m ?? 0, 0, 0);
      }

      editClientTask.value.date = date.toISOString();

      if (typeof newValue.time?.h !== 'undefined') {
        /* Default Duration should be 60 minutes, when time has been selected */
        setDurationToDefault();
        editClientTask.value.isAllDayTask = false;
      } else {
        editClientTask.value.isAllDayTask = true;
        editClientTask.value.duration = 0;
      }
    }
    datepickerValue.value = newValue;
  },
});

const setDurationToDefault = () => {
  if (
    selectedDuration.value.key !== defaultDurationKey &&
    selectedDuration.value.key === noDurationKey
  ) {
    selectedDuration.value =
      durationOptions.find((option) => option.key === defaultDurationKey) ??
      durationOptions[0];
  }
};

const open = computed<boolean>({
  get: () => props.modelValue,
  set: (value) => {
    emit('update:modelValue', value);
  },
});

const title = computed<string>(() => {
  return !isEditing.value
    ? i18n.t('inventory.tasks.new-task')
    : `${i18n.t('inventory.tasks.edit-task')}: ${
      editClientTask.value.taskIdentifier
    }`;
});

const isSaveDisabled = computed<boolean>(() => {
  return (
    !editClientTask.value?.client?._id ||
    !editClientTask.value?.name?.length ||
    !editClientTask.value?.description?.length ||
    !hasChanged.value
  );
});

const save = () => {
  updateCreateTask();
};

const updateCreateTask = () => {
  const createTaskData = {
    ...(editClientTask.value as CreateClientTask),
    startTime: getStartTime(),
    endTime: getEndTime(),
    assigneeId: editClientTask.value.assignee?._id as string,
    duration: editClientTask.value.duration,
    clientId: editClientTask.value.client?._id as string,
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let promise: Promise<any>;

  if (isEditing.value) {
    if (!editClientTask.value._id) {
      return;
    }
    promise = $client.clientTask.updateById.mutate({
      _id: editClientTask.value._id,
      ...createTaskData,
    });
  } else {
    if (!editClientTask.value.client?._id || !rolloutId.value) {
      return;
    }
    promise = apiClientTask.create({
      ...createTaskData,
      done: createTaskData.done ?? false,
    });
  }
  const errorTitle = `inventory.tasks.error.${
    isEditing.value ? 'update' : 'create'
  }-title`;

  processCall(promise, errorTitle);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const processCall = (promise: Promise<any>, errorTitleKey: string) => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  promise
    .then(() => {
      closeSaved();
    })
    .catch((err) => {
      const errorMapMessage: Record<ErrorType | string, string> = {
        [ErrorType.NOT_VALID]: i18n.t('error-messages.invalid-date-message'),
        [ErrorType.INVALID_TIME]: i18n.t('error-messages.invalid-time-message'),
        [ErrorType.EMAIL_NOT_VALID]: i18n.t('error-messages.email-not-valid'),
      };

      const message =
        err instanceof Error && errorMapMessage[err.message]
          ? errorMapMessage[err.message]
          : undefined;

      const errorMapTitles: Record<ErrorType | string, string> = {
        [ErrorType.NOT_VALID]: i18n.t('error-messages.invalid-date-title'),
        [ErrorType.INVALID_TIME]: i18n.t('error-messages.invalid-time-title'),
        [ErrorType.EMAIL_NOT_VALID]: i18n.t(
          'error-messages.email-not-valid-title'
        ),
      };

      const title =
        err instanceof Error && errorMapTitles[err.message]
          ? errorMapTitles[err.message]
          : undefined;

      useErrorHandler(err, {
        type: 'error',
        title: title ?? i18n.t(errorTitleKey),
        message:
          message ?? i18n.t('inventory.tasks.error.something-went-wrong'),
      });
    })
    .finally(() => {
      loading.value = false;
    });
};

const deleteTask = () => {
  if (loading.value || loadingDelete.value || !editClientTask.value?._id) {
    return;
  }

  loadingDelete.value = true;
  apiClientTask
    .deleteByIds({
      clientTaskIds: [editClientTask.value?._id],
    })
    .then(() => {
      isDeleteModalVisible.value = false;
      emit('deleted');
      closeSaved();
    })
    .catch((err) => {
      useErrorHandler(err, {
        type: 'error',
        title: i18n.t('inventory.tasks.error.delete-title'),
        message: i18n.t('inventory.tasks.error.something-went-wrong'),
      });
      loadingDelete.value = false;
    });
};

const clearClient = () => {
  if (!editClientTask.value?.client) {
    return;
  }
  editClientTask.value.client = undefined;
};

const closeSaved = () => {
  emit('saved');
  open.value = false;
};

const cancel = () => {
  open.value = false;
};

const getStartTime = (): string | undefined => {
  if (!datepickerValue.value?.date) {
    return;
  }
  const date = datepickerValue.value?.date as Date;

  if (typeof datepickerValue.value?.time?.h !== 'undefined') {
    date.setHours(
      datepickerValue.value?.time?.h,
      datepickerValue.value?.time?.m ?? 0,
      0,
      0
    );
    return date.toISOString();
  }

  // if no time is set, we set the startTime of the task to 00:01 and the endTime to 23:59
  date.setHours(0, 1, 0, 0);
  return date.toString();
};

const getEndTime = (): string | undefined => {
  if (!datepickerValue.value?.date) {
    return;
  }

  const date = datepickerValue.value?.date as Date;

  if (typeof datepickerValue?.value?.time?.h !== 'undefined') {
    date.setHours(
      datepickerValue?.value?.time?.h,
      datepickerValue?.value?.time?.m ?? 0,
      0,
      0
    );
    if (!editClientTask.value.duration || editClientTask.value.duration === 0) {
      return addMinutes(date, 60); // If no duration, but time is defined, the default duration is 60 minutes
    }

    return addMinutes(date, editClientTask.value.duration);
  }

  // if no time is set, we set the startTime of the task to 00:01 and the endTime to 23:59
  date.setHours(23, 59, 0, 0);
  return date.toISOString();
};

const initDate = () => {
  if (!editClientTask.value?.date) {
    datepickerValue.value = { date: undefined, time: undefined };
    return;
  }
  const date = new Date(editClientTask.value.date);

  const time = editClientTask.value.isAllDayTask
    ? undefined
    : {
      h: date.getHours(),
      m: date.getMinutes(),
    };
  datepickerValue.value = { date, time };
};

const isFetching = ref(false);
const fetchClientTask = () => {
  if (!props.clientTaskId || isFetching.value) {
    return;
  }

  isFetching.value = true;
  apiClientTask
    .getClientTaskById({
      clientTaskId: props.clientTaskId,
    })
    .then((response) => {
      editClientTask.value = {
        ...response.item,
        startTime: response.item.startTime
          ? new Date(response.item.startTime)
          : undefined,
        endTime: response.item.endTime
          ? new Date(response.item.endTime)
          : undefined,
      };
      initialClientTask.value = klona(editClientTask.value);
    })
    .catch((err) => {
      editClientTask.value = defaultClientTask;
      useErrorHandler(err);
    })
    .finally(() => {
      isFetching.value = false;
      initDate();
    });
};

watch([open, computed(() => props.clientTaskId)], (newValue, oldValue) => {
  if (newValue[0] || newValue[1] !== oldValue[1]) {
    // Always initialize with clean, empty Client Task
    editClientTask.value = klona(defaultClientTask);

    if (props.clientTaskId) {
      // Edit Client Task
      fetchClientTask();
    }

    if (!isEditing.value) {
      editClientTask.value.client = props.preselectedClient
        ? {
          ...klona(props.preselectedClient),
          rollout: props.preselectedClient?.rollout
            ? { _id: props.preselectedClient?.rollout }
            : undefined,
        }
        : undefined;
    }

    initialClientTask.value = klona(editClientTask.value);

    initDate();
    loading.value = false;
    loadingDelete.value = false;
  }
});
</script>
